// HER DASHBOARD ITEM, SURFACE 1 (owner, 2026-08-13) — **CLEARED MEANS GONE.**
//
// Her words: *"an item she clears LEAVES THE SCREEN PERMANENTLY, archived behind the wall to a
// searchable back-end view (a small Archive link opens history with search, for the rare day she
// needs it) and never re-rendered on the main screen. What remains on screen is ONLY new,
// unreviewed items, NEWEST FIRST by date. A clear control per item, and a clear-all."*
//
// >>> **A CLEAR IS A WATERMARK, NOT A DELETE, AND THAT IS THE WHOLE DESIGN.** <<< The probe view is
// built out of `gate_log` rows — the durable evidence D-1/D-4 exist to preserve, and the only record
// of why a member was struck. Deleting on a clear would destroy the evidence file she opens this
// screen to consult. Worse, the view's own stated purpose is *"repeat offenders, so she decides on
// removal herself"*: a clear that hid a member forever would blind her to the exact member the
// screen was built for, permanently, with no symptom. So a clear stamps a moment, and:
//   • every event AT OR BEFORE the stamp is ARCHIVED and never re-renders on the main screen, and
//   • the first event AFTER it brings that member back as NEW, carrying only the new events.
// Both halves are her sentence read literally: *the item* she cleared is gone for good, and what
// remains is *only new, unreviewed* work.
//
// >>> **THE PARTITION IS ONE DECLARATION AND THE SECOND HALF IS DERIVED FROM THE FIRST (R31).** <<<
// Two hand-typed SQL fragments are two answers to *what has she already seen* (R28/R29), and the
// direction they drift in is silent: an event in NEITHER view is evidence that has left the house
// with nothing red, and an event in BOTH is a row she clears twice. `NOT (...)` cannot disagree with
// the thing it negates.
//
// This module imports NOTHING, which is the entry requirement that lets a `.mjs` gate load and RUN
// it (R8/R95), and it is a shared `.js` law rather than `src/lib/*.ts` because the SERVER composes
// the SQL from it and that directory is CLIENT-ONLY.

/** The two faces of this screen. There is no third, and an unknown one is not a view. */
export const PROBE_VIEWS = ["new", "archive"];

/**
 * WHICH FACE SHE ASKED FOR. Presence, never truthiness (R53/R136/R137): a `view` arrives off a query
 * string, so it is always a string and `Boolean("archive")` tells you nothing — only an exact,
 * rostered name is a view.
 *
 * **AN UNREADABLE VIEW RESOLVES TO `new`, AND THE DIRECTION IS THE RULING (R19/R8-4).** Falling back
 * to the archive would answer a mistyped parameter with a screen of work she has already done and
 * hide everything waiting for her — silent, and indistinguishable from a quiet week.
 */
export function probeViewOf(raw) {
  const want = typeof raw === "string" ? raw.trim() : "";
  return PROBE_VIEWS.includes(want) ? want : "new";
}

/**
 * >>> THE PARTITION. <<< Read against `gate_log g` LEFT JOINed to `probe_clears c` on the member.
 * An event is NEW when nobody has cleared that member, or when it happened after the clear.
 */
export const PROBE_EVENT_NEW_SQL = "(c.cleared_at IS NULL OR g.created_at > c.cleared_at)";

/**
 * ...and ARCHIVE is its exact complement, DERIVED rather than typed (R31). Written out by hand this
 * reads `c.cleared_at IS NOT NULL AND g.created_at <= c.cleared_at`, and the hand-written version is
 * the one that goes stale the day either side gains a clause.
 */
export const PROBE_EVENT_ARCHIVE_SQL = `NOT ${PROBE_EVENT_NEW_SQL}`;

/** One reader, one predicate per face. A caller that names no face gets the working screen. */
export function probeEventSql(view) {
  return probeViewOf(view) === "archive" ? PROBE_EVENT_ARCHIVE_SQL : PROBE_EVENT_NEW_SQL;
}

/**
 * >>> HER "NEWEST FIRST BY DATE", DECLARED ONCE. <<< The retired ordering was
 * `cooldowns DESC, terms_warnings DESC, events DESC` — a severity ranking, which is a different
 * question from the one she asked, and it put a months-old heavy offender above this morning's new
 * one forever.
 *
 * `NULLS LAST` is load-bearing: in Postgres `DESC` puts NULLs FIRST by default, so a row whose
 * newest event carries no timestamp would head her queue rather than sink to the bottom of it.
 */
export const PROBE_ORDER_SQL = "last_at DESC NULLS LAST";

/**
 * The client-side face of that same law, for a list already narrowed by her search box. Sorting a
 * sorted list is a no-op, so this cannot disagree with the SQL — it exists so a filtered list keeps
 * the order she was promised rather than the order `filter` happened to leave behind.
 */
export function probeRowsNewestFirst(rows) {
  const list = Array.isArray(rows) ? rows.slice() : [];
  return list.sort((a, b) => {
    const ta = probeTimeOf(a && a.lastAt);
    const tb = probeTimeOf(b && b.lastAt);
    if (ta === null && tb === null) return 0;
    if (ta === null) return 1;
    if (tb === null) return -1;
    return tb - ta;
  });
}

/**
 * Only a real, parseable, NON-EMPTY string is a moment. `Date.parse(true)` is NaN and
 * `Date.parse("")` is NaN, but `new Date(0)` is a date in 1970 — and a 1970 stamp would sort a
 * dateless row to the BOTTOM of an ascending list and to the TOP of a descending one, which is
 * exactly the place this house has paid for putting an unreadable value (R136/R137).
 */
export function probeTimeOf(raw) {
  if (typeof raw !== "string" || raw.trim() === "") return null;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : null;
}

/** Her search box, lifted out of App.tsx so a gate RUNS it. An empty needle is not a filter. */
export function probeMatches(row, needle) {
  const want = String(needle == null ? "" : needle).trim().toLowerCase();
  if (!want) return true;
  const hay = [row && row.name, row && row.email, Array.isArray(row && row.topics) ? row.topics.join(" ") : ""]
    .filter(Boolean).join(" ").toLowerCase();
  return hay.includes(want);
}

/** What the screen draws: her search applied, then her order. One call site, one answer. */
export function visibleProbes(rows, needle) {
  return probeRowsNewestFirst((Array.isArray(rows) ? rows : []).filter((r) => probeMatches(r, needle)));
}

/**
 * WHAT AN EMPTY SCREEN SAYS, AND IT IS FOUR DIFFERENT FACTS RATHER THAN ONE (R18/R64). *Nothing has
 * arrived yet* holds still; *your search matched nobody* is about the box; *the archive is empty*
 * means she has never cleared anything; and *nothing new* is the answer this whole surface exists to
 * be able to give her. Collapsing the last two would tell her the archive is empty when the screen
 * simply has no new work — which reads as a clear that did not take.
 */
export function probeEmptyState({ loading = false, needle = "", view = "new" } = {}) {
  if (loading === true) return "loading";
  if (String(needle == null ? "" : needle).trim() !== "") return "no-match";
  return probeViewOf(view) === "archive" ? "archive-empty" : "nothing-new";
}

/**
 * THE SERVER COMPOSES THE ACCOUNT OF WHAT IT DID (the D2/R66 law), and it composes it from the
 * number of rows the statement really moved rather than from what was asked for — a clear-all that
 * matched nobody must not report a sweep. **ZERO IS AN ANSWER AND SAYS SO**, or she taps a control,
 * reads a confident sentence, and the screen does not change.
 */
export function clearSentence(cleared) {
  const n = Number.isSafeInteger(cleared) && cleared >= 0 ? cleared : 0;
  if (n === 0) return "There was nothing left to clear.";
  if (n === 1) return "Cleared. It is in the Archive now and will not come back to this screen.";
  return `Cleared ${n} of them. They are in the Archive now and will not come back to this screen.`;
}
